import { useState, useEffect } from 'react';
import {
  Shield,
  CheckCircle,
  XCircle,
  AlertCircle,
  Search,
  Plus,
  ChevronRight,
  Clock,
  Users,
  X,
  FileCheck,
} from 'lucide-react';
import type { ComplianceControl, ComplianceFramework, ControlStatus } from '../types/compliance';
import './Controls.css';

const frameworks: ComplianceFramework[] = ['SOC2', 'GDPR', 'COPPA', 'CCPA', 'HIPAA', 'ISO27001', 'PCI_DSS'];
const categories = ['Access Control', 'Data Protection', 'Security', 'Availability', 'Monitoring', 'Risk Management', 'General'];

export default function Controls() {
  const [controls, setControls] = useState<ComplianceControl[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFramework, setSelectedFramework] = useState<ComplianceFramework | 'all'>('all');
  const [selectedStatus, setSelectedStatus] = useState<ControlStatus | 'all'>('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedControl, setSelectedControl] = useState<ComplianceControl | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [newControl, setNewControl] = useState({
    id: '',
    name: '',
    description: '',
    framework: 'SOC2' as ComplianceFramework,
    category: 'General',
    owner: 'Mark Pickering',
    affectedProducts: [] as string[],
    notes: '',
  });

  const fetchControls = async () => {
    try {
      const res = await fetch('/api/compliance/controls');
      if (res.ok) {
        const data = await res.json();
        setControls(data);
      } else {
        setError('Failed to load controls');
      }
    } catch (err) {
      console.error('Failed to fetch controls:', err);
      setError('Failed to load controls');
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      setError(null);
      await fetchControls();
      setLoading(false);
    };
    loadData();
  }, []);

  const handleCreateControl = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await fetch('/api/compliance/controls', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...newControl,
          status: 'needs_review',
        }),
      });
      if (res.ok) {
        setShowCreateModal(false);
        setNewControl({
          id: '',
          name: '',
          description: '',
          framework: 'SOC2',
          category: 'General',
          owner: 'Mark Pickering',
          affectedProducts: [],
          notes: '',
        });
        fetchControls();
      }
    } catch (err) {
      console.error('Failed to create control:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateStatus = async (controlId: string, newStatus: ControlStatus) => {
    try {
      const res = await fetch(`/api/compliance/controls/${controlId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus }),
      });
      if (res.ok) {
        fetchControls();
        // Update selected control if viewing detail
        if (selectedControl && selectedControl.id === controlId) {
          setSelectedControl({ ...selectedControl, status: newStatus });
        }
      }
    } catch (err) {
      console.error('Failed to update control status:', err);
    }
  };

  const filteredControls = controls.filter((control) => {
    const matchesSearch =
      control.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      control.id.toLowerCase().includes(searchQuery.toLowerCase()) ||
      control.description.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesFramework = selectedFramework === 'all' || control.framework === selectedFramework;
    const matchesStatus = selectedStatus === 'all' || control.status === selectedStatus;

    return matchesSearch && matchesFramework && matchesStatus;
  });

  const getStatusIcon = (status: ControlStatus) => {
    switch (status) {
      case 'passing':
        return <CheckCircle size={18} className="status-icon passing" />;
      case 'failing':
        return <XCircle size={18} className="status-icon failing" />;
      case 'needs_review':
        return <AlertCircle size={18} className="status-icon warning" />;
      default:
        return <Shield size={18} className="status-icon muted" />;
    }
  };

  const getStatusBadgeClass = (status: ControlStatus) => {
    switch (status) {
      case 'passing': return 'badge-passing';
      case 'failing': return 'badge-failing';
      case 'needs_review': return 'badge-warning';
      default: return 'badge-info';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const stats = {
    total: controls.length,
    passing: controls.filter((c) => c.status === 'passing').length,
    failing: controls.filter((c) => c.status === 'failing').length,
    review: controls.filter((c) => c.status === 'needs_review').length,
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="page-header">
          <h1>Compliance Controls</h1>
        </div>
        <div style={{ textAlign: 'center', padding: '2rem', color: '#a6adc8' }}>
          Loading controls...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-container">
        <div className="page-header">
          <h1>Compliance Controls</h1>
        </div>
        <div style={{ textAlign: 'center', padding: '2rem', color: '#f38ba8' }}>
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <div className="header-content">
          <h1>Compliance Controls</h1>
          <p>Manage and monitor compliance controls across all frameworks</p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowCreateModal(true)}>
          <Plus size={18} />
          Add Control
        </button>
      </div>

      {/* Quick Stats */}
      <div className="control-stats">
        <div className="control-stat">
          <span className="control-stat-value">{stats.total}</span>
          <span className="control-stat-label">Total Controls</span>
        </div>
        <div className="control-stat passing">
          <CheckCircle size={16} />
          <span className="control-stat-value">{stats.passing}</span>
          <span className="control-stat-label">Passing</span>
        </div>
        <div className="control-stat failing">
          <XCircle size={16} />
          <span className="control-stat-value">{stats.failing}</span>
          <span className="control-stat-label">Failing</span>
        </div>
        <div className="control-stat warning">
          <AlertCircle size={16} />
          <span className="control-stat-value">{stats.review}</span>
          <span className="control-stat-label">Needs Review</span>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="controls-toolbar">
        <div className="search-box">
          <Search size={18} />
          <input
            type="text"
            placeholder="Search controls..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <div className="filter-group">
          <select
            value={selectedFramework}
            onChange={(e) => setSelectedFramework(e.target.value as ComplianceFramework | 'all')}
            className="filter-select"
          >
            <option value="all">All Frameworks</option>
            {frameworks.map((fw) => (
              <option key={fw} value={fw}>{fw}</option>
            ))}
          </select>
          <select
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value as ControlStatus | 'all')}
            className="filter-select"
          >
            <option value="all">All Statuses</option>
            <option value="passing">Passing</option>
            <option value="failing">Failing</option>
            <option value="needs_review">Needs Review</option>
            <option value="not_applicable">Not Applicable</option>
          </select>
        </div>
      </div>

      {/* Controls List */}
      <div className="controls-list">
        {filteredControls.map((control) => (
          <div key={control.id} className="control-card">
            <div className="control-header">
              <div className="control-id-section">
                {getStatusIcon(control.status)}
                <span className="control-id">{control.id}</span>
                <span className={`badge ${getStatusBadgeClass(control.status)}`}>
                  {control.status.replace('_', ' ')}
                </span>
              </div>
              <span className="control-framework">{control.framework}</span>
            </div>

            <h3 className="control-name">{control.name}</h3>
            <p className="control-description">{control.description}</p>

            {control.notes && (
              <div className="control-notes">
                <AlertCircle size={14} />
                {control.notes}
              </div>
            )}

            <div className="control-meta">
              <div className="control-meta-item">
                <Clock size={14} />
                <span>Last checked: {formatDate(control.lastChecked)}</span>
              </div>
              <div className="control-meta-item">
                <Users size={14} />
                <span>{control.affectedProducts.length} products</span>
              </div>
              <div className="control-meta-item">
                <Shield size={14} />
                <span>{control.evidenceCount} evidence items</span>
              </div>
            </div>

            <div className="control-products">
              {control.affectedProducts.slice(0, 3).map((product) => (
                <span key={product} className="product-tag">{product}</span>
              ))}
              {control.affectedProducts.length > 3 && (
                <span className="product-tag more">+{control.affectedProducts.length - 3}</span>
              )}
            </div>

            <button className="control-action" onClick={() => setSelectedControl(control)}>
              View Details
              <ChevronRight size={16} />
            </button>
          </div>
        ))}
      </div>

      {filteredControls.length === 0 && (
        <div className="empty-state">
          <Shield size={48} />
          <h3>No controls found</h3>
          <p>Try adjusting your search or filter criteria</p>
        </div>
      )}

      {/* Create Control Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Add New Control</h2>
              <button className="close-btn" onClick={() => setShowCreateModal(false)}>
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleCreateControl}>
              <div className="form-row">
                <div className="form-group">
                  <label>Control ID *</label>
                  <input
                    type="text"
                    value={newControl.id}
                    onChange={(e) => setNewControl({ ...newControl, id: e.target.value })}
                    placeholder="e.g., SOC2-AC-01"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Framework *</label>
                  <select
                    value={newControl.framework}
                    onChange={(e) => setNewControl({ ...newControl, framework: e.target.value as ComplianceFramework })}
                    required
                  >
                    {frameworks.map((fw) => (
                      <option key={fw} value={fw}>{fw}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Control Name *</label>
                <input
                  type="text"
                  value={newControl.name}
                  onChange={(e) => setNewControl({ ...newControl, name: e.target.value })}
                  placeholder="e.g., User Access Review"
                  required
                />
              </div>

              <div className="form-group">
                <label>Description *</label>
                <textarea
                  value={newControl.description}
                  onChange={(e) => setNewControl({ ...newControl, description: e.target.value })}
                  placeholder="Describe this control..."
                  rows={3}
                  required
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Category</label>
                  <select
                    value={newControl.category}
                    onChange={(e) => setNewControl({ ...newControl, category: e.target.value })}
                  >
                    {categories.map((cat) => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Owner</label>
                  <input
                    type="text"
                    value={newControl.owner}
                    onChange={(e) => setNewControl({ ...newControl, owner: e.target.value })}
                    placeholder="Control owner"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Notes</label>
                <textarea
                  value={newControl.notes}
                  onChange={(e) => setNewControl({ ...newControl, notes: e.target.value })}
                  placeholder="Additional notes..."
                  rows={2}
                />
              </div>

              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowCreateModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={submitting}>
                  {submitting ? 'Creating...' : 'Create Control'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Control Detail Modal */}
      {selectedControl && (
        <div className="modal-overlay" onClick={() => setSelectedControl(null)}>
          <div className="modal modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title-row">
                <h2>{selectedControl.id}</h2>
                <span className={`badge ${getStatusBadgeClass(selectedControl.status)}`}>
                  {selectedControl.status.replace('_', ' ')}
                </span>
              </div>
              <button className="close-btn" onClick={() => setSelectedControl(null)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <h3 style={{ marginBottom: '8px' }}>{selectedControl.name}</h3>
              <p style={{ color: 'var(--text-secondary)', marginBottom: '20px' }}>
                {selectedControl.description}
              </p>

              <div className="detail-grid">
                <div className="detail-item">
                  <span className="detail-label">Framework</span>
                  <span>{selectedControl.framework}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Category</span>
                  <span>{selectedControl.category}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Owner</span>
                  <span>{selectedControl.owner}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Last Checked</span>
                  <span>{formatDate(selectedControl.lastChecked)}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Next Review</span>
                  <span>{formatDate(selectedControl.nextReview)}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Evidence Items</span>
                  <span>{selectedControl.evidenceCount}</span>
                </div>
              </div>

              {selectedControl.notes && (
                <div className="detail-section">
                  <h3>Notes</h3>
                  <p>{selectedControl.notes}</p>
                </div>
              )}

              <div className="detail-section">
                <h3>Affected Products</h3>
                <div className="control-products" style={{ marginTop: '8px' }}>
                  {selectedControl.affectedProducts.length > 0 ? (
                    selectedControl.affectedProducts.map((product) => (
                      <span key={product} className="product-tag">{product}</span>
                    ))
                  ) : (
                    <span style={{ color: 'var(--text-muted)' }}>No products assigned</span>
                  )}
                </div>
              </div>

              <div className="detail-section">
                <h3>Update Status</h3>
                <div className="status-actions">
                  <button
                    className="btn btn-success"
                    onClick={() => handleUpdateStatus(selectedControl.id, 'passing')}
                    disabled={selectedControl.status === 'passing'}
                  >
                    <CheckCircle size={16} />
                    Mark Passing
                  </button>
                  <button
                    className="btn btn-danger"
                    onClick={() => handleUpdateStatus(selectedControl.id, 'failing')}
                    disabled={selectedControl.status === 'failing'}
                  >
                    <XCircle size={16} />
                    Mark Failing
                  </button>
                  <button
                    className="btn btn-secondary"
                    onClick={() => handleUpdateStatus(selectedControl.id, 'needs_review')}
                    disabled={selectedControl.status === 'needs_review'}
                  >
                    <AlertCircle size={16} />
                    Needs Review
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
